import { Description } from '@mui/icons-material';
import LoopIcon from '@mui/icons-material/Loop';
import { Box, Chip, Grid, IconButton, Stack, Typography } from '@mui/material';
import { CompanyDocumentResell } from 'lib/api/reSell/factoring/types';
import TableCard from 'lib/designSystem/Card/TableCard';
import SmallHeaderWithIcon from 'lib/designSystem/SmallHeaderWithIcon';
import EnhancedTable from 'lib/designSystem/Table';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import { formatDate, formatMoney } from 'lib/functions/utils';
import { SummaryRow } from 'pages/Finance/Components/Financing/common/Summary';
import React from 'react';

interface DocumentInfoCardProps {
  document: {
    folio: string;
    businessName: string;
    stakeholderName: string;
    amount: number;
    amountConciliated: number;
    totalRemaining: number;
    startingPaymentDate: string;
    paymentDate: string;
    originalPaymentDate?: string;
  };
  resellData: {
    status?: string;
    ownCollectionAt?: string;
    buybackAt?: string;
  };
  canEdit: boolean;
  onEdit: () => void;
  resell: CompanyDocumentResell;
}

const DocumentInfoCard: React.FC<DocumentInfoCardProps> = ({
  document,
  resellData,
  canEdit,
  onEdit,
  resell,
}) => {
  const chipsConfig = [
    {
      key: 'ownCollectionAt',
      show: !!resellData?.ownCollectionAt,
      label: 'Recaudacion propia',
      color: 'primary' as const,
      tooltip: resellData?.ownCollectionAt
        ? formatDate(new Date(resellData.ownCollectionAt))
        : '',
    },
    {
      key: 'buybackAt',
      show: !!resellData?.buybackAt,
      label: 'Recomprada',
      color: 'success' as const,
      tooltip: resellData?.buybackAt
        ? formatDate(new Date(resellData.buybackAt))
        : '',
    },
  ];

  const visibleChips = chipsConfig.filter((chip) => chip.show);

  return (
    <Box>
      <TableCard
        size="small"
        enabledEffects={false}
        sx={{ boxShadow: 'none', background: 'transparent', border: 'none' }}
      >
        <SmallHeaderWithIcon
          icon={<Description fontSize="small" />}
          title="Datos del documento"
        />
        <Grid container spacing={0} sx={{ mb: 2 }}>
          {/* Columna izquierda: Cliente/Deudor y Fechas */}
          <Grid item xs={6}>
            <Box sx={{ pr: 2 }}>
              <SummaryRow
                label="Cliente"
                value={document.businessName}
                labelVariant="body2"
                valueVariant="body2"
                valueMaxLength={30}
              />
              <SummaryRow
                label="Deudor"
                value={document.stakeholderName}
                labelVariant="body2"
                valueVariant="body2"
                valueMaxLength={30}
              />
              <SummaryRow
                label="Vencimiento Original"
                value={
                  document.startingPaymentDate || document.originalPaymentDate
                    ? formatDate(
                        new Date(
                          document.startingPaymentDate ||
                            document.originalPaymentDate ||
                            '',
                        ),
                        'dd-mm-yyyy',
                      )
                    : '-'
                }
                labelVariant="body2"
                valueVariant="body2"
              />
              <SummaryRow
                label="Vencimiento Actual"
                value={formatDate(new Date(document.paymentDate), 'dd-mm-yyyy')}
                labelVariant="body2"
                valueVariant="body2"
                showBorder={false}
              />
            </Box>
          </Grid>

          {/* Columna derecha: Montos */}
          <Grid item xs={6}>
            <Box
              sx={{
                pl: 2,
                borderLeft: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 0.75,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography
                  fontWeight={600}
                  variant="body2"
                  color="text.secondary"
                >
                  Folio
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="body2" color="text.primary">
                    {document.folio}
                  </Typography>
                  {canEdit && (
                    <IconButton size="small" onClick={onEdit} sx={{ p: 0.5 }}>
                      <LoopIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              </Box>
              <SummaryRow
                label="Monto documento"
                value={formatMoney(document.amount)}
                labelVariant="body2"
                valueVariant="body2"
              />
              <SummaryRow
                label="Abonos"
                value={formatMoney(document.amountConciliated)}
                labelVariant="body2"
                valueVariant="body2"
              />
              <SummaryRow
                label="Saldo deuda"
                value={formatMoney(document.totalRemaining)}
                labelVariant="body2"
                valueVariant="body2"
                showBorder={false}
              />
            </Box>
          </Grid>
        </Grid>

        <Stack direction="row" spacing={1} sx={{ mb: 2, mt: 0.5 }}>
          <SmallHeaderWithIcon
            icon={<Description fontSize="small" />}
            title="Saldos de la venta"
          />
          {visibleChips.map((chip) => (
            <LightTooltip
              key={chip.key}
              title={
                <Box sx={{ p: 1 }}>
                  <Typography variant="body2">{chip.tooltip}</Typography>
                </Box>
              }
              placement="bottom"
            >
              <Chip
                size="small"
                label={chip.label}
                color={chip.color}
                variant="outlined"
                sx={{ fontWeight: 'bold' }}
              />
            </LightTooltip>
          ))}
        </Stack>
        <Grid container spacing={0}>
          <Grid item xs={6}>
            <Box sx={{ pr: 2 }}>
              <SummaryRow
                label="Monto venta"
                value={formatMoney(resell.amount)}
                labelVariant="body2"
                valueVariant="body2"
                valueMaxLength={30}
              />
              <SummaryRow
                label="Saldo financiado"
                value={formatMoney(resell.documentAmountRemaining)}
                labelVariant="body2"
                valueVariant="body2"
                valueMaxLength={30}
              />
              <SummaryRow
                label="Saldo mora"
                value={formatMoney(resell.arrearAmountRemaining)}
                labelVariant="body2"
                valueVariant="body2"
              />
              <SummaryRow
                label="Saldo financiado + mora"
                value={formatMoney(resell.netAmountRemaining)}
                labelVariant="body2"
                valueVariant="body2"
                showBorder={false}
              />
            </Box>
          </Grid>
          <Grid item xs={6}>
            <SummaryRow
              label="Monto financiado"
              value={formatMoney(resell.netAmount)}
              labelVariant="body2"
              valueVariant="body2"
            />
            <SummaryRow
              label="Conciliado documento"
              value={formatMoney(resell.documentAmountConciliated)}
              labelVariant="body2"
              valueVariant="body2"
            />
            <SummaryRow
              label="Conciliado mora"
              value={formatMoney(resell.arrearAmountConciliated)}
              labelVariant="body2"
              valueVariant="body2"
            />
            <SummaryRow
              label="Conciliado total"
              value={formatMoney(resell.netAmountConciliated)}
              labelVariant="body2"
              valueVariant="body2"
              showBorder={false}
            />
          </Grid>
        </Grid>

        {resell.orderedConciliations.length > 0 && (
          <>
            <Stack direction="row" spacing={1} sx={{ mb: 2, mt: 0.5 }}>
              <SmallHeaderWithIcon
                icon={<Description fontSize="small" />}
                title="Conciliaciones de la venta"
              />
            </Stack>
            <EnhancedTable
              data={resell.orderedConciliations}
              dataHeaders={[
                {
                  label: 'Descripción tipo',
                  key: 'type',
                  type: 'resell-conciliation-type',
                },
                {
                  label: 'Tipo',
                  key: 'typeEsp',
                },
                { label: 'Fecha', key: 'date', type: 'date' },
                { label: 'Monto', key: 'amount', type: 'money' },
              ]}
              exportToFile="xlsx"
              exportHeaders={[
                {
                  label: 'Tipo',
                  key: 'typeEsp',
                },
                { label: 'Fecha', key: 'date', type: 'date' },
                { label: 'Monto', key: 'amount', type: 'money' },
              ]}
            />
          </>
        )}
      </TableCard>
    </Box>
  );
};

export default DocumentInfoCard;
